import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "lifecycle-for-edge-network-behavior-reference"
    }}>{`Lifecycle for Edge Network behavior reference`}</h1>
    <p>{`The Lifecycle for Edge Network extension introduces a new way of collecting Lifecycle data and tracking sessions. Unlike the previous Lifecycle extension, which used a configurable timeout window for session tracking, this extension emits Application Launch and Close events, treating each pair as an independent session. As a result of not relying on a session timeout window to identify new sessions, the potential Lifecycle states are simplified.`}</p>
    <h2 {...{
      "id": "general-behavior"
    }}>{`General behavior`}</h2>
    <p>{`The Lifecycle for Edge Network extension relies solely on the Lifecycle events dispatched by the `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/api-reference.md#lifecyclestart"
      }}><inlineCode parentName="a">{`lifecycleStart`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/api-reference.md#lifecyclepause"
      }}><inlineCode parentName="a">{`lifecyclePause`}</inlineCode></a>{` calls to determine the start and end of new sessions and to calculate lifecycle data.`}</p>
    <p>{`A Lifecycle session is the time between a `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call and a `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` call. A new session is detected when `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` is called after app first launch or after a `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{`.`}</p>
    <p>{`All events that are not Lifecycle start or Lifecycle pause do not affect Lifecycle state or data. Starting MobileCore using `}<inlineCode parentName="p">{`registerExtensions`}</inlineCode>{` does not automatically start a Lifecycle session nor does it affect Lifecycle data.`}</p>
    <h4 {...{
      "id": "event-reference"
    }}>{`Event reference`}</h4>
    <p>{`Events dispatched by the Lifecycle extension to support Adobe Experience Platform Edge Network workflows include:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../../home/base/mobile-core/lifecycle/event-reference.md#lifecycle-application-foreground"
        }}>{`Lifecycle Application Launch (Foreground)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../../home/base/mobile-core/lifecycle/event-reference.md#lifecycle-application-background"
        }}>{`Lifecycle Application Close (Background)`}</a></li>
    </ul>
    <p>{`For full event details, please read the `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/event-reference.md"
      }}>{`Lifecycle event reference`}</a>{`.`}</p>
    <h4 {...{
      "id": "data-payloads"
    }}>{`Data payloads`}</h4>
    <p>{`The Lifecycle for Edge Network event payloads are defined by the Platform Mobile Lifecycle Details XDM field group, and include information about the application, device, and environment when the event occurred. Please refer to `}<a parentName="p" {...{
        "href": "metrics.md"
      }}>{`Lifecycle for Edge Network data`}</a>{` for the data included with these events.`}</p>
    <h2 {...{
      "id": "expected-lifecycle-scenarios"
    }}>{`Expected Lifecycle scenarios`}</h2>
    <p>{`The following Lifecycle workflows show examples of the expected ordering of Lifecycle events.`}</p>
    <h3 {...{
      "id": "app-launch-and-close"
    }}>{`App launch and close`}</h3>
    <p>{`This scenario shows the standard Lifecycle workflow, with expected Lifecycle API calls.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/b5e0f27e5c72ae6fa68c697329e54907/lifecycle-app-launch-and-close.svg",
        "alt": null
      }}></img></p>
    {/* 
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched"]) ==> graph1
        graph1 ==> D(["3.<br/>App closed by user"])
        D ==> graph2
         subgraph graph1 [" "]
            direction TB
            B("2a.<br/><code>lifecycleStart</code><br/>(Start of new session)") ==> C("2b.<br/>Application Launch (Foreground)")
        end
         subgraph graph2 [" "]
            direction TB
            E(4a.<br/><code>lifecyclePause</code>) ==> F("4b.<br/>Application Close (Background)<br/>(Close type: close)")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class A,D regularPill;
        class B,C,E,F regularBox;
        class graph1,graph2 transparentSubgraph;
     */}
    <h3 {...{
      "id": "on-app-crash-or-force-close"
    }}>{`On app crash or force close`}</h3>
    <p>{`This scenario shows the standard Lifecycle workflow when the application crashes or is force closed, preventing `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` from being called.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/e2ea162b85a1d68463f8322f94e01540/lifecycle-crash.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched"]) ==> graph1
        graph1 ==> D(["3.<br/>App crashed/force closed<br/>(Unable to call <code>lifecyclePause</code>)"])
        D ==> E(["4.<br/>App launched"])
        E ==> graph2
         subgraph graph1 [" "]
            direction TB
            B("2a.<br/><code>lifecycleStart</code><br/>") ==> C("2b.<br/>Application Launch (Foreground)")
        end
         subgraph graph2 [" "]
            direction TB
            F("5a.<br/><code>lifecycleStart</code><br/>") ==> G("5b.<br/>Application Close (Background)<br/>(Close type: unknown)")
            G ==> H("5c.<br/>Application Launch (Foreground)")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class A,D,E regularPill;
        class B,C,F,G,H regularBox;
        class graph1,graph2 transparentSubgraph;
     */}
    <h3 {...{
      "id": "rapid-successive-lifecyclepause-and-lifecyclestart-calls"
    }}>{`Rapid successive lifecyclePause and lifecycleStart calls`}</h3>
    <p>{`This scenario shows the handling of rapid successive calls to `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` followed by `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{`. This is designed to accommodate use cases such as in Android, where Lifecycle APIs are linked to the Android Activity lifecycle callback methods. When users transition between activities within the same app, this logic prevents these transitions from being tracked as individual sessions.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/eb387acfd6fcc8b7b7235ea8288e7ba7/lifecycle-rapid-successive-calls.svg",
        "alt": null
      }}></img></p>
    {/* 
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched<br/>(ex: Activity A)"]) ==> graph1
        graph1 ==> D(["3.<br/>Activity B launched<br/>(Activity A closing)"])
        D ==> graph2
        graph2 == < 0.5 sec ==> graph3
         subgraph graph1 [" "]
            direction TB
            B("2a.<br/><code>lifecycleStart</code><br/>(Activity A)") ==> C("2b.<br/>Application Launch (Foreground)")
        end
         subgraph graph2 [" "]
            direction TB
            E("4a.<br/><code>lifecyclePause</code><br/>(Activity A)") ==> F("4b.<br/>Pause task queued")
        end
         subgraph graph3 [" "]
            direction TB
            G("5a.<br/><code>lifecycleStart</code><br/>(Activity B)") ==> H("5b.<br/>1. Cancels pause task (4b)<br/>2. No Application Launch event<br/>3. No Lifecycle data changes")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class A,D regularPill;
        class B,C,E,F,G,H regularBox;
        class graph1,graph2,graph3 transparentSubgraph;
      */}
    <h2 {...{
      "id": "troubleshooting-unexpected-lifecycle-scenarios"
    }}>{`Troubleshooting unexpected Lifecycle scenarios`}</h2>
    <p>{`The following Lifecycle workflows show examples of the unexpected ordering of Lifecycle events that can impact Lifecycle data and can indicate an incorrect implementation of the Lifecycle APIs. To address the following scenarios, refer to the `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/home/base/mobile-core/lifecycle/index.md"
      }}>{`implementation guide for Lifecycle`}</a>{`.`}</p>
    <h3 {...{
      "id": "consecutive-lifecycle-api-calls"
    }}>{`Consecutive Lifecycle API calls`}</h3>
    <p>{`Consecutive `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` and consecutive `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` API calls will not update their respective timestamps stored in persistence, and Lifecycle data is not changed.`}</p>
    <h4 {...{
      "id": "consecutive-lifecyclestart-api-calls"
    }}>{`Consecutive `}<inlineCode parentName="h4">{`lifecycleStart`}</inlineCode>{` API calls`}</h4>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/822736cf6a76cce90c4b0073fa8cbe6b/lifecycle-start-after-start.svg",
        "alt": null
      }}></img></p>
    {/* 
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        graph1 == "1. App still in memory<br>2. <code>lifecyclePause</code> not called" ==> graph2
         subgraph graph1 [" "]
            direction TB
            B(1a.<br/><code>lifecycleStart</code>) ==> C("1b.<br/>Application Launch (Foreground)")
            class graph1 transparentSubgraph;
        end
         subgraph graph2 [" "]
            direction TB
            E(2a.<br/><code>lifecycleStart</code>) ==> F("2b.<br/>Call ignored, no event dispatched")
            class graph2 transparentSubgraph;
        end
         classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class B,C,F regularBox;
        class E incorrectBox;
     */}
    <h4 {...{
      "id": "consecutive-lifecyclepause-api-calls"
    }}>{`Consecutive `}<inlineCode parentName="h4">{`lifecyclePause`}</inlineCode>{` API calls`}</h4>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/16f3db44f4426c60a1562e0164474ff0/lifecycle-pause-after-pause.svg",
        "alt": null
      }}></img></p>
    {/* 
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        graph1 == "1. App still in memory<br>2. <code>lifecycleStart</code> not called" ==> graph2
         subgraph graph1 [" "]
            direction TB
            B(1a.<br/><code>lifecyclePause</code>) ==> C("1b.<br/>Application Close (Background)")
            class graph1 transparentSubgraph;
        end
         subgraph graph2 [" "]
            direction TB
            E(2a.<br/><code>lifecyclePause</code>) ==> F("2b.<br/>Call ignored, no event dispatched")
            class graph2 transparentSubgraph;
        end
         classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class B,C,F regularBox;
        class E incorrectBox;
     */}
    <h3 {...{
      "id": "missing-pause-app-terminated"
    }}>{`Missing pause, app terminated`}</h3>
    <p>{`This scenario looks the same as a standard crash, but the underlying reason is an incorrect implementation where `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` is not called before the app is terminated.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/d2173d34a0f0b4c7021fd2cd77da52c9/lifecycle-missing-pause-terminated.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched"]) ==> graph1
        graph1 ==> D(["3.<br/>App closed normally<br/>(Incorrect implementation:<br/>Missing <code>lifecyclePause</code>)"])
        D ==> E(["4.<br/>App removed<br/>from memory"])
        E ==> F(["5.<br/>App launched"])
        F ==> graph2
         subgraph graph1 [" "]
            direction TB
            B("2a.<br/><code>lifecycleStart</code><br/>") ==> C("2b.<br/>Application Launch (Foreground)")
        end
         subgraph graph2 [" "]
            direction TB
            G("6a.<br/><code>lifecycleStart</code><br/>") ==> H("6b.<br/>Application Close (Background)<br/>(Close type unknown)")
            H ==> I("6c.<br/>Application Launch (Foreground)")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class A,E,F regularPill;
        class D incorrectBox;
        class B,C,G,H,I regularBox;
        class graph1,graph2 transparentSubgraph;
     */}
    <h3 {...{
      "id": "missing-pause-app-still-in-memory"
    }}>{`Missing pause, app still in memory`}</h3>
    <p>{`This scenario shows an example of `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclestart-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecycleStart`}</inlineCode>{` API calls`}</a>{`. The app close is not detected because `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` is not called. As the app is not removed from memory, the current session continues.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/e64ee3aa8ab3e72d1d539a342fba96d1/lifecycle-missing-pause-not-terminated.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched"]) ==> graph1
        graph1 ==> D(["3.<br/>App closed normally<br/>(Incorrect implementation:<br/>Missing <code>lifecyclePause</code>)"])
        D ==> E(["4.<br/>App launched<br/>(Still in memory)"])
        E ==> graph2
         subgraph graph1 [" "]
            direction TB
            B("2a.<br/><code>lifecycleStart</code><br/>") ==> C("2b.<br/>Application Launch (Foreground)")
        end
         subgraph graph2 [" "]
            direction TB
            F("5a.<br/><code>lifecycleStart</code><br/>(Consecutive start)") ==> G("5b.<br/>Call ignored, no event dispatched")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class A,E regularPill;
        class D incorrectBox;
        class B,C,F,G regularBox;
        class graph1,graph2 transparentSubgraph;
     */}
    <p>{`In this scenario, the `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call (5a) is not detected as a new session nor a crash. It will have the consequence of a `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclestart-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecycleStart`}</inlineCode>{` API call`}</a>{`.`}</p>
    <h3 {...{
      "id": "missing-start"
    }}>{`Missing start`}</h3>
    <p>{`This scenario shows an example of a missing `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` call (1). The new session is not detected because `}<inlineCode parentName="p">{`lifecycleStart`}</inlineCode>{` is not called.`}</p>
    <p><img parentName="p" {...{
        "src": "/client-sdks/assets/728e0a2277d276e597e5c27b14cf6096/lifecycle-missing-start.svg",
        "alt": null
      }}></img></p>
    {/* mermaid.js diagram definition
     %%{
      init: {
        'themeVariables': {
          'lineColor': '#7a7a7a'
        }
      }
     }%%
     graph LR
        A(["1.<br/>App launched<br/>(Incorrect implementation:<br/>Missing <code>lifecycleStart</code>)"]) ==> B(["2.<br/>App closed normally"])
        B ==> graph1
         subgraph graph1 [" "]
            direction TB
            C(3a.<br/><code>lifecyclePause</code>) ==> D("3b.<br/>Application Close (Background)")
        end
         classDef dashedPill fill:#d3d3d3,stroke:#000,stroke-dasharray: 5 5,color:#000;
        classDef regularPill fill:#3273de,stroke:#3273de,color:#fff;
        classDef regularBox fill:#009c3b,stroke:#009c3b,color:#fff;
        classDef incorrectBox fill:#EB1000,stroke:#EB1000,color:#fff;
        classDef transparentSubgraph fill:transparent,stroke:#7a7a7a;
         class B regularPill;
        class A incorrectBox;
        class C,D regularBox;
        class graph1 transparentSubgraph;
     */}
    <p>{`In this scenario, the `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` call (3a) will dispatch an Application Close event only if the Lifecycle extension state is not already set to Pause (that is, `}<inlineCode parentName="p">{`lifecyclePause`}</inlineCode>{` has not been called before the current call). Otherwise, it is effectively a `}<a parentName="p" {...{
        "href": "#consecutive-lifecyclepause-api-calls"
      }}>{`consecutive `}<inlineCode parentName="a">{`lifecyclePause`}</inlineCode>{` API call`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      